/**
 * SmartTemplateWizard Component
 * 5-step wizard for guided template creation with recommendations
 * Steps: 1. Platform  2. Audience  3. Goal  4. Duration  5. Recommendations
 */

import React, { useState } from 'react';
import { Modal, Button, Spin } from 'antd';
import { useAtom, useSetAtom } from 'jotai';
import { Wand2, Eye, Monitor, Smartphone, Square } from 'lucide-react';
import { SiYoutube, SiLinkedin, SiInstagram, SiTiktok, SiUdemy } from 'react-icons/si';
import { Building2, MonitorPlay } from 'lucide-react';
import { WizardProgress } from './WizardProgress';
import { WizardNavigation } from './WizardNavigation';
import { TemplatePreviewModal } from '../TemplatePreviewModal';
import { Template } from '../../../types/template';
import { 
  smartWizardStateAtom, 
  closeWizardAtom, 
  initMakeItYoursAtom,
  Platform, 
  Audience, 
  Goal, 
  Duration,
  TemplateRecommendation
} from '../../../atoms/wizardAtoms';
import { calculateRecommendations } from '../../../services/templateRecommendationService';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../../overlays/UpgradePrompt';
import './SmartTemplateWizard.scss';

interface SmartTemplateWizardProps {
  visible: boolean;
  onClose: () => void;
  onSelectTemplate?: (templateId: string) => void;
  onUseTemplate?: (template: Template) => void;
  onMakeItYours?: (templateId: string) => void;
}

const STEP_LABELS = ['Platform', 'Audience', 'Goal', 'Length', 'Results'];

const PlatformIcon: React.FC<{ platform: Platform }> = ({ platform }) => {
  switch (platform) {
    case 'youtube': return <SiYoutube size={28} color="#FF0000" />;
    case 'linkedin': return <SiLinkedin size={28} color="#0A66C2" />;
    case 'instagram': return <SiInstagram size={28} color="#E4405F" />;
    case 'tiktok': return <SiTiktok size={28} color="#000000" />;
    case 'course': return <SiUdemy size={28} color="#A435F0" />;
    case 'internal': return <Building2 size={28} color="#6B7280" />;
    case 'multiple': return <MonitorPlay size={28} color="#4A3CFA" />;
    default: return null;
  }
};

const PLATFORM_OPTIONS: { value: Platform; label: string; desc: string }[] = [
  { value: 'youtube', label: 'YouTube', desc: 'Long-form video' },
  { value: 'linkedin', label: 'LinkedIn', desc: 'Professional content' },
  { value: 'instagram', label: 'Instagram', desc: 'Reels & Stories' },
  { value: 'tiktok', label: 'TikTok', desc: 'Short-form viral' },
  { value: 'course', label: 'Course', desc: 'Learning platform' },
  { value: 'internal', label: 'Internal', desc: 'Team communication' },
  { value: 'multiple', label: 'Multiple', desc: 'Cross-platform' }
];

const AUDIENCE_OPTIONS: { value: Audience; icon: string; label: string; desc: string }[] = [
  { value: 'customers', icon: '👥', label: 'Customers', desc: 'Prospects & clients' },
  { value: 'investors', icon: '💰', label: 'Investors', desc: 'Funding audience' },
  { value: 'students', icon: '📚', label: 'Students', desc: 'Learners & trainees' },
  { value: 'team', icon: '🏢', label: 'Team', desc: 'Internal colleagues' },
  { value: 'public', icon: '🌍', label: 'Public', desc: 'General audience' },
  { value: 'developers', icon: '💻', label: 'Developers', desc: 'Technical audience' }
];

const GOAL_OPTIONS: { value: Goal; icon: string; label: string; desc: string }[] = [
  { value: 'teach', icon: '🎓', label: 'Teach', desc: 'Educate on a topic' },
  { value: 'demo', icon: '🖥️', label: 'Demo', desc: 'Show how it works' },
  { value: 'sell', icon: '💰', label: 'Sell', desc: 'Pitch a product' },
  { value: 'introduce', icon: '👋', label: 'Introduce', desc: 'Meet the team/company' },
  { value: 'document', icon: '📄', label: 'Document', desc: 'Create reference material' },
  { value: 'testimonial', icon: '⭐', label: 'Testimonial', desc: 'Share success stories' }
];

const DURATION_OPTIONS: { value: Duration; icon: string; label: string; desc: string; time: string }[] = [
  { value: 'quick', icon: '⚡', label: 'Quick', desc: 'Snappy content', time: 'Under 1 min' },
  { value: 'standard', icon: '⏱️', label: 'Standard', desc: 'Typical length', time: '2-5 min' },
  { value: 'detailed', icon: '🎬', label: 'Detailed', desc: 'In-depth coverage', time: '5-10 min' },
  { value: 'longform', icon: '🎥', label: 'Long-form', desc: 'Deep dive', time: '10+ min' }
];

// Map platforms to their expected aspect ratios
const PLATFORM_ASPECT_RATIOS: Record<string, '16:9' | '9:16' | '1:1'> = {
  youtube: '16:9',
  linkedin: '16:9',
  instagram: '9:16',
  tiktok: '9:16',
  course: '16:9',
  internal: '16:9',
  multiple: '16:9'
};

// Get aspect ratio display info
const getAspectRatioInfo = (ratio: '16:9' | '9:16' | '1:1' | undefined) => {
  switch (ratio) {
    case '16:9':
      return { icon: Monitor, label: 'Landscape', className: 'ratio-landscape' };
    case '9:16':
      return { icon: Smartphone, label: 'Portrait', className: 'ratio-portrait' };
    case '1:1':
      return { icon: Square, label: 'Square', className: 'ratio-square' };
    default:
      return { icon: Monitor, label: '16:9', className: 'ratio-landscape' };
  }
};

const getTemplateThumbnail = (templateId: string): string => {
  const thumbnailMap: Record<string, string> = {
    'edu-course-intro': '/templates/course-intro.jpeg',
    'edu-tutorial': '/templates/tutorial-explainer.jpeg',
    'edu-student-testimonial': '/templates/student-testimonial.jpeg',
    'biz-pitch-deck': '/templates/pitch-deck.jpeg',
    'biz-product-demo': '/templates/product-demo.jpeg',
    'biz-company-culture': '/templates/company-culture.jpeg',
    'content-youtube-opener': '/templates/youtube-opener.jpeg',
    'content-social-promo': '/templates/social-promo.jpeg',
    'dev-code-tutorial': '/templates/code-tutorial.jpeg',
    'dev-api-docs': '/templates/api-doc.jpeg',
    'interview-intro': '/templates/candidate-intro.jpeg',
    'interview-remote-setup': '/templates/remote-interview.jpeg',
  };
  return thumbnailMap[templateId] || '/templates/custom.jpeg';
};

export const SmartTemplateWizard: React.FC<SmartTemplateWizardProps> = ({
  visible,
  onClose,
  onSelectTemplate,
  onUseTemplate,
  onMakeItYours
}) => {
  const [state, setState] = useAtom(smartWizardStateAtom);
  const closeWizard = useSetAtom(closeWizardAtom);
  const initMakeItYours = useSetAtom(initMakeItYoursAtom);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const { isPaid } = useFeatureAccess();

  const handleClose = () => {
    closeWizard();
    setState({
      currentStep: 1,
      platform: null,
      audience: null,
      goal: null,
      duration: null,
      recommendations: [],
      isCalculating: false
    });
    onClose();
  };

  const handleNext = async () => {
    if (state.currentStep < 5) {
      const nextStep = (state.currentStep + 1) as 1 | 2 | 3 | 4 | 5;
      
      if (nextStep === 5 && state.platform && state.audience && state.goal && state.duration) {
        setState({ ...state, currentStep: nextStep, isCalculating: true });
        try {
          const recs = await calculateRecommendations({
            platform: state.platform,
            audience: state.audience,
            goal: state.goal,
            duration: state.duration
          });
          setState(prev => ({ ...prev, recommendations: recs, isCalculating: false }));
        } catch (error) {
          console.error('Failed to calculate recommendations:', error);
          setState(prev => ({ ...prev, isCalculating: false }));
        }
      } else {
        setState({ ...state, currentStep: nextStep });
      }
    }
  };

  const handleBack = () => {
    if (state.currentStep > 1) {
      setState({ ...state, currentStep: (state.currentStep - 1) as 1 | 2 | 3 | 4 | 5 });
    }
  };

  const handleUseTemplate = (rec: TemplateRecommendation) => {
    onUseTemplate?.(rec.template);
  };

  const handleMakeItYoursClick = (rec: TemplateRecommendation) => {
    // Gate feature for non-paid users
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    initMakeItYours(rec.template);
    onMakeItYours?.(rec.templateId);
  };

  const canProceed = () => {
    switch (state.currentStep) {
      case 1: return state.platform !== null;
      case 2: return state.audience !== null;
      case 3: return state.goal !== null;
      case 4: return state.duration !== null;
      case 5: return true;
      default: return false;
    }
  };

  const renderOptionCard = (
    option: { value: string; icon: React.ReactNode; label: string; desc: string; time?: string },
    selectedValue: string | null,
    onSelect: (value: any) => void
  ) => (
    <div 
      key={option.value}
      className={`wizard-option-card ${selectedValue === option.value ? 'selected' : ''}`}
      onClick={() => onSelect(option.value)}
    >
      <span className="option-icon">
        {typeof option.icon === 'string' ? option.icon : option.icon}
      </span>
      <div className="option-title">{option.label}</div>
      <div className="option-subtitle">{option.desc}</div>
      {option.time && <div className="option-time">{option.time}</div>}
    </div>
  );

  const renderPlatformCard = (
    option: { value: Platform; label: string; desc: string },
    selectedValue: Platform | null,
    onSelect: (value: Platform) => void
  ) => (
    <div 
      key={option.value}
      className={`wizard-option-card ${selectedValue === option.value ? 'selected' : ''}`}
      onClick={() => onSelect(option.value)}
    >
      <span className="option-icon">
        <PlatformIcon platform={option.value} />
      </span>
      <div className="option-title">{option.label}</div>
      <div className="option-subtitle">{option.desc}</div>
    </div>
  );

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Where Will This Video Be Shared?</h3>
      <p className="wizard-step-subtitle">We'll optimize the format for your platform</p>
      <div className="wizard-option-grid">
        {PLATFORM_OPTIONS.map(opt => renderPlatformCard(opt, state.platform, (v) => setState({ ...state, platform: v })))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Who Is Your Audience?</h3>
      <p className="wizard-step-subtitle">We'll match the tone and complexity</p>
      <div className="wizard-option-grid">
        {AUDIENCE_OPTIONS.map(opt => renderOptionCard(opt, state.audience, (v) => setState({ ...state, audience: v })))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">What's Your Main Goal?</h3>
      <p className="wizard-step-subtitle">We'll structure the content to achieve it</p>
      <div className="wizard-option-grid">
        {GOAL_OPTIONS.map(opt => renderOptionCard(opt, state.goal, (v) => setState({ ...state, goal: v })))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">How Long Should It Be?</h3>
      <p className="wizard-step-subtitle">We'll pick templates with the right pacing</p>
      <div className="wizard-option-grid duration-grid">
        {DURATION_OPTIONS.map(opt => renderOptionCard(opt, state.duration, (v) => setState({ ...state, duration: v })))}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="wizard-step step-recommendations">
      <h3 className="wizard-step-title">Your Perfect Templates</h3>
      <p className="wizard-step-subtitle">Based on your answers, here are our top picks</p>
      
      {state.isCalculating ? (
        <div className="calculating-loader">
          <Spin size="large" />
          <p>Finding your perfect templates...</p>
        </div>
      ) : state.recommendations.length === 0 ? (
        <div className="no-recommendations">
          <p>No exact matches found. Try adjusting your preferences.</p>
          <Button onClick={() => setState({ ...state, currentStep: 1 })}>
            Start Over
          </Button>
        </div>
      ) : (
        <div className="recommendations-list">
          {state.recommendations.map((rec, index) => (
            <div key={rec.templateId} className={`recommendation-card ${index === 0 ? 'top-pick' : ''}`}>
              {index === 0 && <span className="top-pick-badge">🏆 Best Match</span>}
              <div className="recommendation-content">
                <div 
                  className="recommendation-thumbnail"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewTemplate(rec.template);
                  }}
                  title="Click to preview"
                >
                  <img 
                    src={getTemplateThumbnail(rec.template.id)} 
                    alt={rec.template.title}
                    loading="lazy"
                  />
                  <div className="thumbnail-overlay">
                    <Eye size={20} />
                  </div>
                </div>
                <div className="recommendation-info">
                  <div className="recommendation-header">
                    <div className="recommendation-title">
                      {rec.template.title}
                    </div>
                    <div className="match-score">{rec.score}% match</div>
                  </div>
                  
                  <div className="recommendation-meta">
                    <span>🎬 {rec.template.scenes.length} scenes</span>
                    <span>⏱️ {Math.ceil(rec.template.scenes.reduce((s, sc) => s + sc.suggestedDuration, 0) / 60)} min</span>
                    {(() => {
                      const templateRatio = rec.template.aspectRatio || '16:9';
                      const expectedRatio = state.platform ? PLATFORM_ASPECT_RATIOS[state.platform] : '16:9';
                      const ratioInfo = getAspectRatioInfo(templateRatio);
                      const isMatch = templateRatio === expectedRatio;
                      const RatioIcon = ratioInfo.icon;
                      return (
                        <span className={`ratio-badge ${ratioInfo.className} ${isMatch ? 'ratio-match' : ''}`}>
                          <RatioIcon size={12} />
                          {templateRatio}
                        </span>
                      );
                    })()}
                  </div>
                  
                  <div className="recommendation-reasons">
                    {rec.matchReasons.map((reason, i) => (
                      <span key={i} className="reason-tag">{reason}</span>
                    ))}
                  </div>
                  
                  <div className="recommendation-actions">
                    <Button 
                      onClick={() => handleUseTemplate(rec)}
                    >
                      Use As-Is
                    </Button>
                    <Button 
                      type="primary"
                      onClick={() => handleMakeItYoursClick(rec)}
                    >
                      Make It Yours
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={680}
      className="wizard-modal smart-template-modal"
      destroyOnHidden
      title={
        <div className="wizard-header">
          <Wand2 size={20} className="header-icon" />
          Find Your Perfect Template
        </div>
      }
    >
      <WizardProgress 
        currentStep={state.currentStep} 
        totalSteps={5}
        stepLabels={STEP_LABELS}
      />
      
      <div className="wizard-content">
        {state.currentStep === 1 && renderStep1()}
        {state.currentStep === 2 && renderStep2()}
        {state.currentStep === 3 && renderStep3()}
        {state.currentStep === 4 && renderStep4()}
        {state.currentStep === 5 && renderStep5()}
      </div>
      
      {state.currentStep < 5 && (
        <WizardNavigation
          currentStep={state.currentStep}
          totalSteps={5}
          onBack={handleBack}
          onNext={handleNext}
          canProceed={canProceed()}
          nextLabel={state.currentStep === 4 ? 'See Recommendations' : 'Next'}
        />
      )}
      
      <TemplatePreviewModal
        template={previewTemplate}
        visible={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onUseTemplate={() => {
          if (previewTemplate) {
            const rec = state.recommendations.find(r => r.template.id === previewTemplate.id);
            if (rec) handleUseTemplate(rec);
          }
        }}
        onMakeItYours={() => {
          if (previewTemplate) {
            const rec = state.recommendations.find(r => r.template.id === previewTemplate.id);
            if (rec) handleMakeItYoursClick(rec);
          }
        }}
        onCustomizeTemplate={() => setPreviewTemplate(null)}
      />
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI-Guided Personalization"
        description="Make It Yours uses AI to personalize templates based on your needs. Upgrade to Pro to unlock this feature and create custom templates tailored to your content."
      />
    </Modal>
  );
};

export default SmartTemplateWizard;
